import React, { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import useUser from 'hooks/use-user';
import useInstoreCart from 'hooks/use-instore-cart';

import { tracker } from 'utils/analytics';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { AerosyncPreAuthProvider } from 'src/payments/components/aerosync-pre-auth/context/aerosync-pre-auth-provider';

import { InstoreCartLayout } from 'src/payments/layout/instore-cart/layout';
import InstoreCartPage from 'src/payments/instore-cart/views/instore-cart/instore-cart';
import { BarcodeView } from 'src/payments/instore-cart/views/barcode/barcode';

import { instoreCartPollingInterval } from 'src/payments/constants';
import { PBB_PAYMENT_SWITCH_FLAG, useLatchedFlag } from 'src/payments/dutchie-pay/flags';
import { CartViews } from 'src/payments/state/instore-cart';
import { isSafeRelativeUrl } from 'src/payments/utils/is-safe-relative-url';
import { useCartRouting } from './hooks/use-cart-routing';
import { useCartApproval } from './hooks/use-cart-approval';
import { useInstoreCartDataSync } from './hooks/use-instore-cart-data-sync';
import { useNotifyInstoreCartOpened } from './hooks/use-notify-instore-cart-opened';
import { LoginForm, CompleteAccount } from '../views';
import { CartLoader, ErrorLoadingInvoiceHeader } from '../components';

const PAYMENT_COMPLETE_REDIRECT_DELAY_MS = 1500;

export function InstoreCartControllerWrapper(): JSX.Element {
  return (
    <AerosyncPreAuthProvider>
      <InstoreCartController />
    </AerosyncPreAuthProvider>
  );
}

function InstoreCartController(): JSX.Element {
  const router = useRouter();
  const pbbPaymentSwitchEnabled = useLatchedFlag(PBB_PAYMENT_SWITCH_FLAG);
  const User = useUser();
  const dispensaryIdQueryString = router?.query.dispensaryId;
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const lastName = useObserver(() => User.lastName);
  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const isMissingUserInfo = !lastName || !phone || !birthday;

  const InstoreCart = useInstoreCart();

  const currentView = useObserver(() => InstoreCart.currentView);
  const isValidCartUser = useObserver(() => InstoreCart.isValidCartUser);
  const cartToken = useObserver(() => InstoreCart.cartToken);
  const tipAmount = useObserver(() => InstoreCart.tip);

  const { LOADING, LOGIN, BARCODE, CART, ERROR, COMPLETE_ACCOUNT } = CartViews;
  const { isEnrolledDutchiePay, enrollmentStatusPending } = useDutchiePay();
  const userNotenrolled = !isUserLoading && isLoggedIn && !isEnrolledDutchiePay;

  let cartTokenTyped: string | null;
  if (Array.isArray(cartToken)) {
    cartTokenTyped = cartToken[0] ?? '';
  } else {
    cartTokenTyped = cartToken || '';
  }
  const { cartData, cartLoading, cartError, dispensaryInfo, startPolling, stopPolling } = useInstoreCartDataSync({
    skip: !isEnrolledDutchiePay,
    cartToken: cartTokenTyped,
  });

  const { paymentLoading, paymentComplete, paymentUnsuccessful, onCartApprove } = useCartApproval({
    cartData,
    dispensaryInfo,
    tipAmount,
    userId: User.id,
    stopPolling,
  });

  const dispensaryIdFromQuery =
    (Array.isArray(dispensaryIdQueryString) ? dispensaryIdQueryString[0] : dispensaryIdQueryString) ?? null;
  const dispensaryId = dispensaryIdFromQuery ?? dispensaryInfo?.dispensaryId ?? null;
  const orderIdParam = router?.query.orderId;
  const orderId = (Array.isArray(orderIdParam) ? orderIdParam[0] : orderIdParam) ?? null;
  const cartTokenQuery = router?.query.cartToken;
  const cartTokenFromQuery = (Array.isArray(cartTokenQuery) ? cartTokenQuery[0] : cartTokenQuery) ?? null;
  const notifyCartToken = cartTokenTyped || cartTokenFromQuery;
  const isKioskQuery = router?.query.isKiosk;
  const isHubQuery = router?.query.isHub;
  const isKioskOrHubFlow =
    (Array.isArray(isKioskQuery) ? isKioskQuery[0] : isKioskQuery) === 'true' ||
    (Array.isArray(isHubQuery) ? isHubQuery[0] : isHubQuery) === 'true';
  useNotifyInstoreCartOpened(notifyCartToken, dispensaryId, orderId, isKioskOrHubFlow, isLoggedIn);

  useCartRouting({
    isUserLoading,
    isLoggedIn,
    isEnrolledDutchiePay,
    enrollmentStatusPending,
    userNotenrolled,
    isMissingUserInfo,
    dispensaryIdQueryString,
  });

  // Router and Query Parameter Effects
  useEffect(() => {
    const { cartToken: cartTokenParam, checkoutToken, isKiosk, isHub } = router?.query ?? {};

    if (cartTokenParam) {
      InstoreCart.setCartToken(cartTokenParam);
    }

    if (checkoutToken && typeof checkoutToken === 'string') {
      InstoreCart.setCheckoutToken(checkoutToken);
    }

    InstoreCart.setIsKiosk(isKiosk === 'true');
    InstoreCart.setIsHub(isHub === 'true');
  }, [router?.query, InstoreCart]);

  // Analytics and Tracking Effects
  useEffect(() => {
    tracker.setContext({
      isKioskOrder: InstoreCart.isKiosk,
      checkoutToken: InstoreCart.checkoutToken,
      isHubOrder: InstoreCart.isHub,
    });
  }, [InstoreCart.isKiosk, InstoreCart.checkoutToken, InstoreCart.isHub]);

  // Fire the per-channel CTA-click telemetry event once on the success landing.
  // The /pay-by-bank success redirect forwards source + orderId + messageStage;
  // gate on a known channel so unrelated arrivals at the in-store cart don't emit a funnel event.
  const sourceQuery = router?.query.source;
  const ctaSource = Array.isArray(sourceQuery) ? sourceQuery[0] : sourceQuery;
  const orderIdQuery = router?.query.orderId;
  const ctaOrderId = Array.isArray(orderIdQuery) ? orderIdQuery[0] : orderIdQuery;
  const messageStageQuery = router?.query.message_stage;
  const ctaMessageStage = Array.isArray(messageStageQuery) ? messageStageQuery[0] : messageStageQuery;
  // useCartRouting stamps entry_point=post_signup on the /pay/signup refUrl; a direct landing has no
  // marker. Normalize to the two known values so a stray query value can't pollute the dimension.
  const entryPointQuery = router?.query.entry_point;
  const ctaEntryPoint =
    (Array.isArray(entryPointQuery) ? entryPointQuery[0] : entryPointQuery) === 'post_signup'
      ? 'post_signup'
      : 'direct';
  const ctaFiredRef = useRef(false);
  useEffect(() => {
    if (ctaFiredRef.current) {
      return;
    }
    if ((ctaSource === 'email_cta' || ctaSource === 'sms_cta') && ctaOrderId) {
      tracker.dutchiePayLinkToPayInitiated({
        source: ctaSource,
        orderId: ctaOrderId,
        messageStage: ctaMessageStage,
        entryPoint: ctaEntryPoint,
      });
      ctaFiredRef.current = true;
    }
  }, [ctaSource, ctaOrderId, ctaMessageStage, ctaEntryPoint]);

  // Cart Data Management Effects
  useEffect(() => {
    InstoreCart.dataRequested([cartLoading, isUserLoading]);
  }, [InstoreCart, cartLoading, isUserLoading]);

  useEffect(() => {
    if (cartData?.tipCents) {
      InstoreCart.tip = cartData.tipCents;
    }
  }, [InstoreCart, cartData?.tipCents]);

  // User Status Effects
  useEffect(() => {
    InstoreCart.userDataReceived(isMissingUserInfo, isLoggedIn, isUserLoading, isEnrolledDutchiePay, cartToken);
  }, [InstoreCart, User, isEnrolledDutchiePay, cartToken, isMissingUserInfo, isLoggedIn, isUserLoading]);

  // Cart Polling Effects
  useEffect(() => {
    if (isValidCartUser && !cartData && !cartToken) {
      startPolling(instoreCartPollingInterval);
    }

    if (cartError) {
      stopPolling();
    }
  }, [cartData, cartError, isValidCartUser, startPolling, stopPolling, cartToken]);

  // Post-payment refUrl redirect. `onCartApprove` strips the URL on click before
  // `paymentComplete` flips, so we pin refUrl on the first render where it's present.
  // `router` is excluded from deps because `useRouter()` returns a fresh reference on every
  // query/asPath change, which would cancel the pending timer via cleanup before it fires.
  const refUrlQuery = router?.query.refUrl;
  const refUrlFromQuery = Array.isArray(refUrlQuery) ? refUrlQuery[0] : refUrlQuery;
  const pinnedRefUrlRef = useRef<string | undefined>(undefined);
  if (refUrlFromQuery && isSafeRelativeUrl(refUrlFromQuery) && !pinnedRefUrlRef.current) {
    pinnedRefUrlRef.current = refUrlFromQuery;
  }
  useEffect(() => {
    if (!pbbPaymentSwitchEnabled) {
      return undefined;
    }
    const refUrl = pinnedRefUrlRef.current;
    if (!paymentComplete || !refUrl) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      void router?.replace(refUrl);
    }, PAYMENT_COMPLETE_REDIRECT_DELAY_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router intentionally excluded
  }, [paymentComplete, pbbPaymentSwitchEnabled]);

  const views = {
    [LOADING]: <CartLoader />,
    [LOGIN]: <LoginForm />,
    [BARCODE]: <BarcodeView />,
    [ERROR]: <ErrorLoadingInvoiceHeader />,
    [CART]: (
      <InstoreCartPage
        onCartApprove={onCartApprove}
        paymentLoading={paymentLoading}
        paymentComplete={paymentComplete}
        paymentUnsuccessful={paymentUnsuccessful}
      />
    ),
    [COMPLETE_ACCOUNT]: <CompleteAccount />,
  };

  return <InstoreCartLayout darkBg={currentView === BARCODE}>{views[currentView]}</InstoreCartLayout>;
}
